import React, { useEffect } from "react";
import { ArrowLeft, MapPin, Phone, Mail, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

const Impressum = () => {
  const navigate = useNavigate();
  
  // Set page-specific SEO
  useEffect(() => {
    document.title = "Impressum | Floria Maler & Sanierung";
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Impressum von Floria Maler & Sanierung. Rechtliche Angaben gemäß § 5 TMG. Aixheimerstrasse 50/4, 78554 Aldingen, Baden-Württemberg.');
    }
    
    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://malerbwfestpreis.de/impressum');
    }
    
    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', 'Impressum | Floria Maler & Sanierung');
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', 'https://malerbwfestpreis.de/impressum');
    
    // Cleanup on unmount
    return () => {
      document.title = "Maler Baden-Württemberg | Wohnung streichen zum Festpreis";
      if (metaDesc) {
        metaDesc.setAttribute('content', 'Maler in Baden-Württemberg zum Festpreis: Wohnung streichen inkl. Material und Abdeckarbeiten. Airless-Technik. Angebot schnell per WhatsApp anfragen.');
      }
      if (canonical) {
        canonical.setAttribute('href', 'https://malerbwfestpreis.de/');
      }
    };
  }, []);
  
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-orange-500 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-white hover:bg-white/20 mr-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zurück
            </Button>
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold">Impressum</h1>
          <p className="text-blue-100 text-lg mt-2">Rechtliche Angaben gemäß § 5 TMG</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Anbieter */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <MapPin className="mr-3 h-6 w-6 text-blue-600" />
            Anbieter
          </h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Firmenname:</h3>
              <p className="text-gray-700">Floria Maler & Sanierung</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Anschrift:</h3>
              <p className="text-gray-700">
                Aixheimerstrasse 50/4<br />
                78554 Aldingen<br />
                Deutschland
              </p>
            </div>
          </div>
        </div>

        {/* Kontakt */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Phone className="mr-3 h-6 w-6 text-blue-600" />
            Kontakt
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center">
              <Phone className="mr-3 h-5 w-5 text-gray-400" />
              <div>
                <p className="font-semibold text-gray-900">Telefon:</p>
                <a href="tel:+4915758183212" className="text-blue-600 hover:text-blue-700">
                  +49 157 58183212
                </a>
              </div>
            </div>
            
            <div className="flex items-center">
              <ExternalLink className="mr-3 h-5 w-5 text-gray-400" />
              <div>
                <p className="font-semibold text-gray-900">WhatsApp:</p>
                <a 
                  href="https://wa.me/4915758183212" 
                  className="text-blue-600 hover:text-blue-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Direkt kontaktieren
                </a>
              </div>
            </div>
            
            <div className="flex items-center">
              <Mail className="mr-3 h-5 w-5 text-gray-400" />
              <div>
                <p className="font-semibold text-gray-900">Website:</p>
                <a href="https://malerbwfestpreis.de" className="text-blue-600 hover:text-blue-700">
                  malerbwfestpreis.de
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Berufsbezeichnung */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Berufsbezeichnung</h2>
          <div className="space-y-4">
            <p className="text-gray-700">
              <strong>Berufsbezeichnung:</strong> Maler und Lackierer
            </p>
            <p className="text-gray-700">
              <strong>Verliehen in:</strong> Deutschland
            </p>
          </div>
        </div>

        {/* Steuerliche Angaben */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Steuerliche Angaben</h2>
          <div className="space-y-4">
            <p className="text-gray-700">
              <strong>Umsatzsteuer-Identifikationsnummer:</strong> [Wird nachgereicht]
            </p>
            <p className="text-gray-700">
              <strong>Steuernummer:</strong> [Wird nachgereicht]
            </p>
            <p className="text-gray-700">
              <strong>Zuständiges Finanzamt:</strong> [Wird nachgereicht]
            </p>
          </div>
        </div>

        {/* Haftungsausschluss */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Haftungsausschluss</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Haftung für Inhalte</h3>
              <p className="text-gray-700">
                Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht unter der Verpflichtung, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Haftung für Links</h3>
              <p className="text-gray-700">
                Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Urheberrecht</h3>
              <p className="text-gray-700">
                Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
              </p>
            </div>
          </div>
        </div>

        {/* Service-Gebiete */}
        <div className="bg-blue-50 rounded-xl p-8 border border-blue-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Unsere Service-Gebiete</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Baden-Württemberg:</h3>
              <ul className="text-gray-700 space-y-1">
                <li>• Stuttgart und Umgebung</li>
                <li>• Konstanz und Bodenseekreis</li>
                <li>• Singen und Hegau</li>
                <li>• Tübingen und Landkreis</li>
                <li>• Aldingen und Region</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Unsere Leistungen:</h3>
              <ul className="text-gray-700 space-y-1">
                <li>• Innenanstriche</li>
                <li>• Außenanstriche</li>
                <li>• Airless-Spritztechnik</li>
                <li>• Renovierung & Sanierung</li>
                <li>• Festpreis-Garantie</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Stand */}
        <div className="text-center mt-12 pt-8 border-t border-gray-200">
          <p className="text-gray-500">
            Stand: {new Date().toLocaleDateString('de-DE', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Impressum;